import React, { type ComponentProps } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { ScrollView, StyleSheet } from 'react-native';

import { BodyText, Button, DisplayText, IllusDelivery_1, Image, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleNoLocation = (props: Props) => {
  const { onSelectLocation } = props;

  const { formatMessage } = useIntl();

  return (
    <ScrollView contentContainerStyle={styles.contentContainer}>
      <Image
        alt={formatMessage(messages.illustrationA11y)}
        source={IllusDelivery_1}
        contentFit="contain"
        style={styles.illustration}
      />

      <DisplayText accessibilityRole="header" sizeMatch={TITLE_SIZE_MATCH} style={styles.title}>
        <FormattedMessage {...messages.title} />
      </DisplayText>

      <BodyText sizeMatch={DESCRIPTION_SIZE_MATCH} style={styles.description}>
        <FormattedMessage {...messages.description} />
      </BodyText>

      <Button
        palette="primary"
        size="large"
        style={styles.selectLocationButton}
        onPress={onSelectLocation}
      >
        <FormattedMessage {...messages.selectLocation} />
      </Button>
    </ScrollView>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ILLUSTRATION_SIZE = 200;
const SELECT_LOCATION_BUTTON_MIN_WIDTH = 193;

const TITLE_SIZE_MATCH: ComponentProps<typeof DisplayText>['sizeMatch'] = ['24'];
const DESCRIPTION_SIZE_MATCH: ComponentProps<typeof BodyText>['sizeMatch'] = ['16'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  contentContainer: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing['10'],
    paddingHorizontal: theme.spacing['6'],
  },
  illustration: {
    width: ILLUSTRATION_SIZE,
    height: ILLUSTRATION_SIZE,
  },
  title: {
    marginVertical: theme.spacing['4'],
    textAlign: 'center',
  },
  description: {
    marginBottom: theme.spacing['10'],
    textAlign: 'center',
  },
  selectLocationButton: {
    minWidth: SELECT_LOCATION_BUTTON_MIN_WIDTH,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  illustrationA11y: {
    defaultMessage: 'Delivery courier riding a scooter',
    description: 'Bundle | No location | Illustration accessibility label',
  },
  title: {
    defaultMessage: 'Where are you ordering from?',
    description: 'Bundle | No location | Title',
  },
  description: {
    defaultMessage: 'Pick a location to see what’s in this bundle.',
    description: 'Bundle | No location | Description',
  },
  selectLocation: {
    defaultMessage: 'Select location',
    description: 'Bundle | No location | Select location CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  onSelectLocation: () => void;
};
